import moment from 'moment'
import { API, graphqlOperation } from 'aws-amplify'
import { ofType } from 'redux-observable'
import { defer, empty, forkJoin, from, of } from 'rxjs'
import { catchError, filter, map, mergeMap, pluck, switchMap, withLatestFrom } from 'rxjs/operators'
import Actions from '../actions'
import i18n from 'resources/locales/i18n'
import { updateGrinUser } from 'graphql/mutations'
import { logError } from 'utils/logUtils'
import { trackEvent } from 'utils/analyticsUtils'
import {
  searchPatientSearchModelsForPmsCreateOrAdd,
  searchPatientSearchModelsForPmsRedirect
} from 'graphql/customQueries'
import { SEARCH_RESULT_LIMIT } from 'consts/patientsConsts'
import { buildPatientScopeFilter, buildPatientSearchFilter, MAX_SEARCH_LEN } from 'utils/searchUtils'
import { classifyPmsPatientMatches, PmsMatchOutcomes, PMS_BIRTH_DATE_FORMATS } from 'utils/pmsPatientMatchUtils'
import { scrubGraphqlError } from 'utils/pmsErrorUtils'
import { ROUTES } from 'consts'
import { getAccessToken } from 'utils/storageUtils'

export const pmsAuthenticationEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_AUTHENTICATION),
    pluck('payload'),
    mergeMap(({ pmsType, pmsToken, onBehalfOf }) => {
      if (pmsToken) {
        return of(Actions.pmsAuthenticationReceived({ access_token: pmsToken }))
      }

      const queryString = onBehalfOf ? `?onBehalfOf=${encodeURIComponent(onBehalfOf)}` : ''

      return from(API.get('grinServerlessApi', `/pms/v1/${pmsType}/auth${queryString}`)).pipe(
        mergeMap(data => of(Actions.pmsAuthenticationReceived(data))),
        catchError(err => of(Actions.pmsAuthenticationFailed(err)))
      )
    })
  )

export const pmsAuthenticationReceivedEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_AUTHENTICATION_RECEIVED),
    withLatestFrom(state$),
    switchMap(([{ payload }, { pmsReducer }]) => {
      localStorage.setItem(`${pmsReducer.pmsType}_token`, payload.access_token)

      return empty()
    })
  )

export const pmsFetchPatientsEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_FETCH_PATIENTS),
    withLatestFrom(state$),
    switchMap(([{ payload: searchValue }, { pmsReducer }]) =>
      from(
        API.get('grinServerlessApi', `/pms/v1/${pmsReducer.pmsType}/patients?searchInput=${searchValue}`, {
          headers: {
            'x-api-key': getAccessToken(),
            'pms-token': localStorage.getItem(`${pmsReducer.pmsType}_token`)
          }
        })
      ).pipe(
        mergeMap(({ patients }) => of(Actions.pmsFetchPatientsReceived({ patients }))),
        catchError(err => of(Actions.pmsFetchPatientsFailed(err)))
      )
    )
  )

export const pmsRequestFailedEpic = action$ =>
  action$.pipe(
    ofType(
      Actions.PMS_FETCH_PATIENTS_FAILED,
      Actions.PMS_LINK_PATIENT_FAILED,
      Actions.PMS_UNLINK_PATIENT_FAILED,
      Actions.PMS_FETCH_PATIENT_FAILED,
      Actions.PMS_FETCH_APPOINTMENTS_FAILED,
      Actions.PMS_FETCH_AVAILABLE_TIME_SLOTS_FAILED,
      Actions.PMS_CREATE_NEW_APPOINTMENT_FAILED,
      Actions.PMS_DELETE_APPOINTMENT_FAILED,
      Actions.PMS_FETCH_STATIC_DATA_FAILED,
      Actions.PMS_AUTHENTICATION_FAILED
    ),
    map(({ payload: error }) => {
      logError('An error occured while integrating with pms', error)

      const errorData = error?.response?.data

      return Actions.showSnackbar({
        type: 'warning',
        text:
          errorData?.code === 'PMS_DOWN'
            ? i18n.t('dialogs.patientInfo.appointments.pms.dolphinDownError')
            : errorData?.message?.toLowerCase()?.includes('unknown error')
            ? i18n.t('dialogs.patientInfo.appointments.pms.unknownPmsError')
            : i18n.t('messages.somethingWentWrongContactSupport')
      })
    })
  )

export const pmsLinkPatientEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_LINK_PATIENT),
    withLatestFrom(state$),
    map(([{ payload }, { patientsReducer }]) => {
      // The Appointments-tab link row acts on the patient card that is open. The PMS quick link
      // dispatches before any card has loaded, so it passes the grin user it already read; taking the
      // card's user there would link whichever patient happened to be open last.
      const patientGrinUser = payload.grinUser || patientsReducer.patientCard?.patient?.user

      return {
        patientGrinUser,
        patientExternalIds: JSON.parse(patientGrinUser?.externalIds || '{}'),
        pmsPatientId: payload.pmsPatientId,
        pmsType: payload.pmsType,
        isSilent: !!payload?.isSilent
      }
    }),
    filter(({ patientGrinUser }) => !!patientGrinUser?.id),
    switchMap(({ patientGrinUser, pmsPatientId, pmsType, patientExternalIds, isSilent }) =>
      from(
        API.graphql(
          graphqlOperation(updateGrinUser, {
            input: {
              id: patientGrinUser.id,
              _version: patientGrinUser._version,
              externalIds: JSON.stringify({ ...patientExternalIds, [pmsType]: pmsPatientId })
            }
          })
        )
      ).pipe(
        mergeMap(response => of(Actions.pmsLinkPatientReceived({ user: response.data.updateGrinUser, isSilent }))),
        catchError(err => of(Actions.pmsLinkPatientFailed(err)))
      )
    )
  )

export const pmsLinkPatientReceviedEpic = action$ =>
  action$.pipe(
    ofType(Actions.PMS_LINK_PATIENT_RECEIVED),
    switchMap(({ payload }) => {
      if (!payload.isSilent) {
        return of(
          Actions.showSnackbar({
            type: 'success',
            text: i18n.t('pms.linkedPatientSuccessMessage')
          })
        )
      }

      return empty()
    })
  )

export const pmsUnlinkPatientEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_UNLINK_PATIENT),
    withLatestFrom(state$),
    map(([{ payload }, { patientsReducer }]) => {
      const patientGrinUser = patientsReducer.patientCard.patient.user
      let patientExternalIds = JSON.parse(patientGrinUser.externalIds || '{}')

      delete patientExternalIds[payload.pmsType]

      return {
        patientGrinUser,
        patientExternalIds: JSON.stringify(patientExternalIds),
        pmsType: payload.pmsType
      }
    }),
    switchMap(({ patientGrinUser, pmsType, patientExternalIds }) =>
      from(
        API.graphql(
          graphqlOperation(updateGrinUser, {
            input: {
              id: patientGrinUser.id,
              _version: patientGrinUser._version,
              externalIds: patientExternalIds
            }
          })
        )
      ).pipe(
        mergeMap(response => of(Actions.pmsUnlinkPatientReceived(response.data.updateGrinUser))),
        catchError(err => of(Actions.pmsUnlinkPatientFailed(err)))
      )
    )
  )

export const pmsFetchAppointmentsEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_FETCH_APPOINTMENTS),
    withLatestFrom(state$),
    switchMap(([{ payload }, { pmsReducer }]) =>
      from(
        API.get('grinServerlessApi', `/pms/v1/${pmsReducer.pmsType}/appointments?patientId=${payload}`, {
          headers: {
            'x-api-key': getAccessToken(),
            'pms-token': localStorage.getItem(`${pmsReducer.pmsType}_token`)
          }
        })
      ).pipe(
        mergeMap(({ appointments }) => of(Actions.pmsFetchAppointmentsReceived({ appointments }))),
        catchError(err => of(Actions.pmsFetchAppointmentsFailed(err)))
      )
    )
  )

export const pmsFetchPatientEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_FETCH_PATIENT),
    withLatestFrom(state$),
    switchMap(([{ payload: patientId }, { pmsReducer }]) =>
      from(
        API.get('grinServerlessApi', `/pms/v1/${pmsReducer.pmsType}/patients/${patientId}`, {
          headers: {
            'x-api-key': getAccessToken(),
            'pms-token': localStorage.getItem(`${pmsReducer.pmsType}_token`)
          }
        })
      ).pipe(
        mergeMap(({ patient, appointments }) => of(Actions.pmsFetchPatientReceived({ patient, appointments }))),
        catchError(err => of(Actions.pmsFetchPatientFailed(err)))
      )
    )
  )

export const pmsFetchAvailableTimeSlotsEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_FETCH_AVAILABLE_TIME_SLOTS),
    withLatestFrom(state$),
    switchMap(([{ payload: queryParams }, { pmsReducer }]) =>
      from(
        API.get('grinServerlessApi', `/pms/v1/${pmsReducer.pmsType}/appointments/timeslots?${queryParams}`, {
          headers: {
            'x-api-key': getAccessToken(),
            'pms-token': localStorage.getItem(`${pmsReducer.pmsType}_token`)
          }
        })
      ).pipe(
        mergeMap(({ availableTimeSlots }) => of(Actions.pmsFetchAvailableTimeSlotsReceived({ availableTimeSlots }))),
        catchError(err => of(Actions.pmsFetchAvailableTimeSlotsFailed(err)))
      )
    )
  )

export const pmsCreateNewAppointmentEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_CREATE_NEW_APPOINTMENT),
    withLatestFrom(state$),
    switchMap(([{ payload: appointmentData }, { pmsReducer }]) =>
      from(
        API.post('grinServerlessApi', `/pms/v1/${pmsReducer.pmsType}/appointments`, {
          body: {
            appointmentData
          },
          headers: {
            'x-api-key': getAccessToken(),
            'pms-token': localStorage.getItem(`${pmsReducer.pmsType}_token`)
          }
        })
      ).pipe(
        mergeMap(({ appointment }) => of(Actions.pmsCreateNewAppointmentReceived({ appointment }))),
        catchError(err => of(Actions.pmsCreateNewAppointmentFailed(err)))
      )
    )
  )

export const pmsCreateNewAppointmentReceivedEpic = action$ =>
  action$.pipe(
    ofType(Actions.PMS_CREATE_NEW_APPOINTMENT_RECEIVED),
    map(() =>
      Actions.showSnackbar({
        type: 'success',
        text: i18n.t('pms.newAppointmentSuccessMessage')
      })
    )
  )

export const pmsDeleteAppointmentEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_DELETE_APPOINTMENT),
    withLatestFrom(state$),
    switchMap(([{ payload: appointmentId }, { pmsReducer }]) =>
      from(
        API.del('grinServerlessApi', `/pms/v1/${pmsReducer.pmsType}/appointments/${appointmentId}`, {
          headers: {
            'x-api-key': getAccessToken(),
            'pms-token': localStorage.getItem(`${pmsReducer.pmsType}_token`)
          }
        })
      ).pipe(
        mergeMap(() => of(Actions.pmsDeleteAppointmentReceived())),
        catchError(err => of(Actions.pmsDeleteAppointmentFailed(err)))
      )
    )
  )

export const pmsDeleteAppointmentReceivedEpic = action$ =>
  action$.pipe(
    ofType(Actions.PMS_DELETE_APPOINTMENT_RECEIVED),
    map(() =>
      Actions.showSnackbar({
        type: 'success',
        text: i18n.t('pms.deleteAppointmentSuccessMessage')
      })
    )
  )

export const pmsFetchStaticDataEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_FETCH_STATIC_DATA),
    withLatestFrom(state$),
    switchMap(([, { pmsReducer }]) =>
      from(
        API.get('grinServerlessApi', `/pms/v1/${pmsReducer.pmsType}/staticData`, {
          headers: {
            'x-api-key': getAccessToken(),
            'pms-token': localStorage.getItem(`${pmsReducer.pmsType}_token`)
          }
        })
      ).pipe(
        mergeMap(staticData => of(Actions.pmsFetchStaticDataReceived(staticData))),
        catchError(err => of(Actions.pmsFetchStaticDataFailed(err)))
      )
    )
  )

export const pmsAutomaticLinkingAttemptEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_AUTOMATIC_LINKING_ATTEMPT),
    withLatestFrom(state$),
    switchMap(([{ payload }, { pmsReducer }]) =>
      from(
        API.post('grinServerlessApi', `/pms/v1/${pmsReducer.pmsType}/patients/autolink`, {
          headers: {
            'x-api-key': getAccessToken(),
            'pms-token': localStorage.getItem(`${pmsReducer.pmsType}_token`)
          },
          body: {
            patientDetails: payload
          }
        })
      ).pipe(
        mergeMap(response => {
          if (response?.matchingPatient) {
            trackEvent('Dolphin linkage - auto patient linking succeeded', {
              matchingParameter: response?.matchingParameter
            })
            return of(
              Actions.pmsLinkPatient({
                pmsPatientId: response.matchingPatient.id,
                pmsType: pmsReducer.pmsType,
                isSilent: true
              })
            )
          }

          trackEvent('Dolphin linkage - patient auto linking failed - no match was found')
          return empty()
        }),
        catchError(err => of(Actions.pmsAutomaticLinkingAttemptFailed(err)))
      )
    )
  )

export const pmsFetchLatestPatientsEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_FETCH_LATEST_PATIENTS),
    withLatestFrom(state$),
    switchMap(([, { pmsReducer }]) =>
      from(
        API.get(
          'grinServerlessApi',
          `/pms/v1/${pmsReducer.pmsType}/patients/latest?latestTimestamp=${moment()
            .subtract('months', 2)
            .toISOString()}`,
          {
            headers: {
              'x-api-key': getAccessToken(),
              'pms-token': localStorage.getItem(`${pmsReducer.pmsType}_token`)
            }
          }
        )
      ).pipe(
        mergeMap(patientsToInvite => of(Actions.pmsFetchLatestPatientsReceived(patientsToInvite))),
        catchError(err => of(Actions.pmsFetchLatestPatientsFailed(err)))
      )
    )
  )

export const pmsFetchLatestPatientsFailedEpic = action$ =>
  action$.pipe(
    ofType(Actions.PMS_FETCH_LATEST_PATIENTS_FAILED),
    switchMap(({ payload: error }) => {
      logError('An error occured while trying to fetch latest patients for doctor', error)
      return empty()
    })
  )

/**
 * GR-10741: close the loop after a patient reaches Grin from a PMS quick link.
 *
 * The quick link for an unlinked patient offers to create or invite one. Nothing then tells the PMS which
 * Grin patient it became, so the practice would see the new patient in Grin but no Grin data in the PMS.
 * Write the link as soon as the patient exists.
 *
 * This used to be covered on Greyfinch's side by an appointment automation, which also made the practice
 * fill in a configuration screen on connect. The automation is gone as of manifest 1.6.0 and this call is
 * what replaces it, so it is now the ONLY thing that links a patient outside a full Sync.
 *
 * Best-effort on purpose: the patient HAS been created and the doctor has already been told so. A failed
 * link is recoverable (a Sync still links it later), so it must not surface as an error over a flow that
 * succeeded — it is logged, not shown.
 *
 * @param {string} grinPatientId the patient that now exists in Grin
 * @param {{ pms: string, pmsPatientId: string }} pmsLink which PMS patient it was opened for
 * @param {string} epicName for the log line, so the two entry points stay distinguishable
 */
const linkPatientBackToPms = ({ grinPatientId, pmsLink, epicName }) =>
  from(
    API.post('grinServerlessApi', `/pms/v1/greyfinch/patients/${grinPatientId}/link`, {
      body: { greyfinchPatientId: pmsLink.pmsPatientId }
    })
  ).pipe(
    mergeMap(() => empty()),
    catchError(err => {
      logError(`${epicName}: could not link the patient back to the PMS`, { patientId: grinPatientId, error: err })

      return of(Actions.pmsLinkCreatedPatientFailed(scrubGraphqlError(err)))
    })
  )

// Only for the integration this was specified for, and only when the PMS said which patient it came from.
// `pms` comes from a vendor-controlled URL, so it is matched against a known value rather than trusted.
const carriesGreyfinchLink = pmsLink => !!pmsLink?.pmsPatientId && pmsLink.pms?.toLowerCase() === GREYFINCH_PMS

export const pmsLinkCreatedPatientEpic = action$ =>
  action$.pipe(
    ofType(Actions.CREATE_RECORDS_PATIENT_RECEIVED),
    pluck('payload'),
    // The context rides on the creation that carried it, so there is no shared slot for a second quick
    // link to overwrite while this request is in flight, and nothing an unrelated later creation could
    // inherit.
    filter(({ id, pmsLink } = {}) => !!id && carriesGreyfinchLink(pmsLink)),
    mergeMap(({ id, pmsLink }) =>
      linkPatientBackToPms({ grinPatientId: id, pmsLink, epicName: 'pmsLinkCreatedPatientEpic' })
    )
  )

/**
 * The same link-back for the INVITE branch of the quick link, which is the path Greyfinch asked for when
 * the automation was dropped: a patient invited from their patient page should come back linked.
 *
 * Note the id: the invite action reports the new patient as `patientId`, not `id`.
 */
export const pmsLinkInvitedPatientEpic = action$ =>
  action$.pipe(
    ofType(Actions.INVITE_NEW_PATIENT_RECEIVED),
    pluck('payload'),
    filter(({ patientId, pmsLink } = {}) => !!patientId && carriesGreyfinchLink(pmsLink)),
    mergeMap(({ patientId, pmsLink }) =>
      linkPatientBackToPms({ grinPatientId: patientId, pmsLink, epicName: 'pmsLinkInvitedPatientEpic' })
    )
  )

export const pmsRedirectFailedEpic = action$ =>
  action$.pipe(
    ofType(Actions.PMS_REDIRECT_FAILED),
    map(({ payload: error }) => {
      logError('An error occured while redirecting to Grin from Dolphin', { error })

      return Actions.showSnackbar({
        type: 'error',
        text: i18n.t('pms.redirectPatientNotFoundModal.error')
      })
    })
  )

/**
 * Wrap a patient-search filter in an AND with the caller's own scope.
 *
 * `buildPatientSearchFilter` returns a bare `or` and only spreads the scope into two of its four
 * branches, so an exact `id` or `username` hit is unscoped. The API is transformer v1, where
 * `@auth` rules are not applied to `@searchable` result filtering, so nothing backstops that
 * server-side. Applied here rather than in the shared helper because other callers pass a term the
 * user typed, while this one takes it from a URL an outside vendor controls.
 */
const scopeFilterToDoctor = (filter, doctorUsername, allowedGroups) => {
  const scope = buildPatientScopeFilter(doctorUsername, allowedGroups)

  return scope ? { and: [filter, scope] } : filter
}

// The Greyfinch integration is the only PMS whose link is ALSO written on the vendor's side, through our
// own service; the Grin-side record is the same externalIds entry every PMS uses (pmsLinkPatientEpic).
const GREYFINCH_PMS = 'greyfinch'

// The PMS id a patient is already linked to, or '' — `externalIds` is a JSON scalar on the Grin user.
const readLinkedPmsId = (candidate, pms) => {
  if (!candidate?.grinUser?.externalIds || !pms) {
    return ''
  }

  try {
    return JSON.parse(candidate.grinUser.externalIds)[pms.toLowerCase()] || ''
  } catch (err) {
    return ''
  }
}

// The identity branches: the Grin id the PMS stored at link time, and the PMS's own patient id matched
// against the link recorded on the Grin user. Either one names a patient outright, which is what makes
// the second click onwards an identity lookup rather than a demographic guess.
const buildIdentityBranches = ({ grinPatientId, pmsPatientId }) => [
  ...(grinPatientId ? [{ id: { eq: grinPatientId } }] : []),
  ...(pmsPatientId ? [{ externalIdsList: { match: pmsPatientId } }] : [])
]

// An identity lookup returns at most a row or two; it is asked separately from the name search so it
// cannot be crowded out of that search's page.
const IDENTITY_RESULT_LIMIT = 5

// A PMS-supplied Grin patient id is only worth querying when it is shaped like one. Anything else is
// dropped rather than sent to OpenSearch, so a malformed vendor value degrades to the name match
// instead of widening the filter.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const readGrinPatientId = value => (typeof value === 'string' && UUID_RE.test(value.trim()) ? value.trim() : '')

/**
 * Combined `action=createoradd` PMS quick link: one URL that opens an existing patient when it can
 * identify exactly one, and otherwise falls through to creating or disambiguating.
 *
 * Candidates come from the same filter the rest of the app searches patients with, so "similar
 * name" means the same thing here as it does in the patients list.
 */
export const pmsCreateOrAddSearchPatientEpic = (action$, state$, { history }) =>
  action$.pipe(
    ofType(Actions.PMS_CREATE_OR_ADD_SEARCH_PATIENT),
    withLatestFrom(state$),
    switchMap(([{ payload }, state]) => {
      const { doctor } = state.profileReducer
      // Bound the term before it reaches OpenSearch. buildPatientSearchFilter does no bounding of
      // its own, and this one comes straight from a URL an outside vendor controls.
      const searchTerm = [payload.firstName, payload.lastName].filter(Boolean).join(' ').trim().slice(0, MAX_SEARCH_LEN)
      // Sent by a PMS that stored our patient id when the patient was linked (Greyfinch keeps it in
      // its `grinPatientId` external mapping). It identifies the patient outright, so it saves the
      // name + birth-date match from having to be confident.
      const grinPatientId = readGrinPatientId(payload.grinPatientId)

      // Defence in depth for the scoping above: an unscoped filter would search across practices.
      if (!doctor?.username) {
        history.push(ROUTES.PATIENTS)

        return of(Actions.pmsRedirectFailed({ message: 'createoradd dispatched before the doctor scope was ready' }))
      }

      if (!searchTerm && !grinPatientId) {
        // Navigate before reporting: Landing renders a permanent loader, so failing in place
        // would strand the user on a spinner behind the error snackbar.
        history.push(ROUTES.PATIENTS)

        return of(
          Actions.pmsRedirectFailed({
            message: 'Missing patient name and Grin patient id for the createoradd action'
          })
        )
      }

      // Parse before building the request. A throw inside the graphqlOperation arguments happens
      // before the observable exists, so the catchError below could not see it — it would escape
      // switchMap and kill this epic for the rest of the session.
      let allowedGroups
      try {
        allowedGroups = JSON.parse(doctor?.user?.allowed_groups_permissions || '[]')
      } catch (err) {
        history.push(ROUTES.PATIENTS)

        return of(
          Actions.pmsRedirectFailed({
            message: 'Malformed allowed_groups_permissions for the doctor',
            name: err?.name
          })
        )
      }

      // scopeFilterToDoctor wraps EVERY filter. buildPatientSearchFilter spreads the scope into its
      // name and email branches only — its `id` and `username` branches are unscoped, and these
      // parameters come from a URL an outside vendor controls, so an unscoped branch is exactly the
      // cross-practice read to avoid.
      const search = (filter, limit) =>
        from(
          API.graphql(
            graphqlOperation(searchPatientSearchModelsForPmsCreateOrAdd, {
              limit,
              filter: scopeFilterToDoctor(filter, doctor?.username, allowedGroups),
              // Deterministic ordering so which candidates survive the limit is stable.
              sort: {
                field: 'lastMessageTimestamp',
                direction: 'desc'
              }
            })
          )
        )

      const identityBranches = buildIdentityBranches({ grinPatientId, pmsPatientId: payload.patientId })

      // Two requests, not one. Folded into the name search, an identity hit competes for the same ten
      // rows: ten same-named patients with newer messages would push the linked patient out, the flow
      // would treat a valid id as absent, and it would offer to create a duplicate of a patient it is
      // already linked to. Asking separately keeps the identity lookup independent of that page.
      return forkJoin([
        identityBranches.length ? search({ or: identityBranches }, IDENTITY_RESULT_LIMIT) : of(null),
        searchTerm
          ? search(buildPatientSearchFilter(searchTerm, doctor?.username, allowedGroups), SEARCH_RESULT_LIMIT)
          : of(null)
      ]).pipe(
        mergeMap(([identityResponse, nameResponse]) => {
          const identityItems = identityResponse?.data?.searchPatientSearchModels?.items || []
          const nameResults = nameResponse?.data?.searchPatientSearchModels
          const nameItems = nameResults?.items || []
          const identityIds = new Set(identityItems.map(candidate => candidate.id))

          return of(
            Actions.pmsCreateOrAddSearchPatientReceived({
              ...payload,
              grinPatientId,
              candidates: [...identityItems, ...nameItems.filter(candidate => !identityIds.has(candidate.id))],
              // Truncation describes the NAME search only — it is what feeds the demographic
              // classifier, and an identity lookup cannot be truncated in any meaningful sense.
              // Fails closed: an absent `total` means the server did not tell us how many matches
              // exist, and a full page is indistinguishable from a truncated one.
              resultsTruncated: nameResponse
                ? nameResults?.total == null ||
                  nameResults.total > SEARCH_RESULT_LIMIT ||
                  nameItems.length >= SEARCH_RESULT_LIMIT
                : false
            })
          )
        }),
        catchError(err => {
          // Landing renders a permanent loader, so navigate before reporting or the doctor is
          // stranded on a spinner behind the snackbar.
          history.push(ROUTES.PATIENTS)

          return of(Actions.pmsRedirectFailed(scrubGraphqlError(err)))
        })
      )
    })
  )

export const pmsCreateOrAddSearchPatientReceivedEpic = (action$, state$, { history }) =>
  action$.pipe(
    ofType(Actions.PMS_CREATE_OR_ADD_SEARCH_PATIENT_RECEIVED),
    pluck('payload'),
    // defer + a per-action catchError, deliberately: a catchError on the outer pipe would emit its
    // fallback and then COMPLETE the stream, leaving the epic deaf to every later action for the
    // rest of the session. Errors have to be contained to the action that caused them.
    mergeMap(payload =>
      // Destructure inside the defer, not in the parameter list: a throw during destructuring
      // would happen before defer runs and would escape the containment below.
      defer(() => {
        const {
          firstName,
          lastName,
          birthDate,
          patientId,
          grinPatientId,
          source,
          canCreatePatient,
          candidates,
          resultsTruncated
        } = payload || {}
        const candidateList = candidates || []

        // An id the PMS stored at link time beats the demographic match: it names one patient
        // instead of inferring them. Only honoured when that patient came back from the scoped
        // query, so a stale id — or one belonging to another practice — cannot open anything and
        // falls through to the name + birth-date match below.
        // Two ways to name a patient outright instead of inferring them: the Grin id the PMS stored
        // at link time, or our own record of which PMS patient this Grin patient is. Either beats the
        // demographic match; both are confirmed against the scoped results, never taken on trust.
        const linkedPatient =
          (grinPatientId && candidateList.find(candidate => candidate.id === grinPatientId)) ||
          (patientId && source && candidateList.find(candidate => readLinkedPmsId(candidate, source) === patientId)) ||
          null

        if (linkedPatient) {
          trackEvent('PMS Create or Add - resolved', {
            outcome: 'open-linked',
            candidateCount: candidateList.length,
            resultsTruncated: !!resultsTruncated,
            canCreatePatient,
            source
          })
          history.push(`${ROUTES.PATIENTS}/${linkedPatient.id}`)

          return empty()
        }
        const { outcome, matchedPatient, suggestions, exactMatches } = classifyPmsPatientMatches({
          firstName,
          lastName,
          birthDate,
          candidates: candidateList,
          resultsTruncated
        })

        const patientName = [firstName, lastName].filter(Boolean).join(' ').trim()
        const parsedBirthDate = moment(birthDate, PMS_BIRTH_DATE_FORMATS, true)
        const createInitialValues = {
          firstName: firstName || '',
          lastName: lastName || '',
          birthDate: parsedBirthDate.isValid() ? parsedBirthDate.toDate() : null,
          note: patientId || '',
          // Which PMS patient this creation is for, so the link back can be written once the patient
          // exists. It travels WITH the creation rather than sitting in the reducer: the modal reloads
          // its values from here every time it opens, so abandoning the flow leaves nothing behind for
          // an unrelated later creation to inherit.
          ...(source && patientId ? { pmsLink: { pms: source, pmsPatientId: patientId } } : {})
        }

        trackEvent('PMS Create or Add - resolved', {
          outcome,
          candidateCount: candidateList.length,
          resultsTruncated: !!resultsTruncated,
          canCreatePatient,
          source
        })

        if (outcome === PmsMatchOutcomes.Open) {
          history.push(`${ROUTES.PATIENTS}/${matchedPatient.id}`)

          // The demographics identified this patient, so record the pairing: every later click on the
          // same PMS link resolves by identity instead of re-running the guess, and the panel stops
          // depending on the name and birth date staying in step across the two systems.
          //
          // Only when the PMS said which patient it came from and this one is not linked yet. The bar
          // for writing it is the classifier's Open outcome — one candidate, birth date confirming the
          // name — the same standard the server-side matcher uses before it links.
          // Only for the integration this was specified for: `source` comes from a vendor-controlled
          // URL, and it becomes the externalIds key, so it is matched against a known value rather
          // than written through. Dolphin has its own explicit link row.
          return patientId && source?.toLowerCase() === GREYFINCH_PMS && !readLinkedPmsId(matchedPatient, source)
            ? of(
                Actions.pmsLinkPatient({
                  grinUser: matchedPatient.grinUser,
                  pmsPatientId: patientId,
                  pmsType: GREYFINCH_PMS,
                  isSilent: true
                })
              )
            : empty()
        }

        history.push(ROUTES.PATIENTS)

        if (outcome === PmsMatchOutcomes.Create) {
          // Without the plan flag the create modal is not mounted at all, so fall back to the
          // existing not-found modal rather than dispatching into the void.
          return canCreatePatient
            ? of(Actions.setCreateRecordsPatientModalOpen({ isOpen: true, initialValues: createInitialValues }))
            : of(Actions.pmsToggleRedirectPatientNotFoundModal({ isOpen: true, pmsPatientName: patientName }))
        }

        return of(
          Actions.pmsToggleCreateOrAddModal({
            isOpen: true,
            patientName,
            suggestions,
            // Two or more equally exact matches land here too, and need different wording from
            // "we couldn't find an exact match".
            exactMatchCount: exactMatches.length,
            createInitialValues: canCreatePatient ? createInitialValues : null
          })
        )
      }).pipe(
        catchError(err => {
          history.push(ROUTES.PATIENTS)

          return of(Actions.pmsRedirectFailed(scrubGraphqlError(err)))
        })
      )
    )
  )

export const pmsRedirectSearchLinkedPatientEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_REDIRECT_SEARCH_LINKED_PATIENT),
    pluck('payload'),
    switchMap(({ pmsPatientId, pmsPatientName }) =>
      from(
        API.graphql(
          graphqlOperation(searchPatientSearchModelsForPmsRedirect, {
            filter: {
              or: [
                {
                  externalIdsList: {
                    match: pmsPatientId
                  }
                },
                {
                  name: {
                    matchPhrasePrefix: pmsPatientName
                  }
                }
              ]
            }
          })
        )
      ).pipe(
        mergeMap(response =>
          of(
            Actions.pmsRedirectSearchLinkedPatientReceived({
              patientsResults: response.data.searchPatientSearchModels?.items,
              pmsPatientName
            })
          )
        ),
        catchError(err => of(Actions.pmsRedirectFailed(err)))
      )
    )
  )

export const pmsRedirectSearchLinkedPatientReceivedEpic = (action$, state$, { history }) =>
  action$.pipe(
    ofType(Actions.PMS_REDIRECT_SEARCH_LINKED_PATIENT_RECEIVED),
    pluck('payload'),
    mergeMap(({ patientsResults, pmsPatientName }) => {
      switch (patientsResults.length) {
        case 0:
          history.push(ROUTES.PATIENTS)
          return of(Actions.pmsToggleRedirectPatientNotFoundModal({ isOpen: true, pmsPatientName }))
        case 1:
          history.push(`${ROUTES.PATIENTS}/${patientsResults[0].id}`)
          return empty()
        default:
          history.push(`${ROUTES.PATIENTS}`)
          return of(Actions.setPatientsPresetFilter({ name: pmsPatientName }))
      }
    }),
    catchError(err => of(Actions.pmsRedirectFailed(err)))
  )
